// src/pages/StockReport.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { getStockReport } from "../services/stockReportService";
import * as XLSX from "xlsx";

function useDebouncedValue(value, delay = 200) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function StockReport() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [fromDate, setFromDate] = useState(monthStart.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(monthEnd.toISOString().slice(0, 10));
  const debouncedFromDate = useDebouncedValue(fromDate, 450);
  const debouncedToDate = useDebouncedValue(toDate, 450);
  const [rowCount, setRowCount] = useState(0);

  const filterRefs = useRef({});

  /* ---------------- Load stock from uploaded sales import ---------------- */
  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await getStockReport(debouncedFromDate || null, debouncedToDate || null);
        if (!mounted) return;
        setRowCount(rows.length);
        setItems(
          rows.map((r, i) => ({
            id: r.code || `stock-${i}`,
            code: r.code,
            desc: r.desc,
            group: r.group,
            stock: r.qty,
            unit: r.unit,
            stdCost: r.rate,
            gst: r.gst,
            brand: r.brand,
          }))
        );
      } catch (err) {
        console.error("StockReport load failed:", err);
        setError(err?.message || "Failed to load stock from uploaded sales data.");
        setItems([]);
        setRowCount(0);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [debouncedFromDate, debouncedToDate]);

  /* ---------------- Columns ---------------- */
  const columns = useMemo(
    () => [
      { key: "code", label: "Particulars", type: "string" },
      { key: "desc", label: "Description", type: "string" },
      { key: "group", label: "Group Name", type: "string" },
      { key: "stock", label: "Tot. Qty", type: "number" },
      { key: "unit", label: "Unit", type: "string" },
      { key: "stdCost", label: "Rate", type: "number" },
      {
        key: "value",
        label: "Value",
        type: "number",
        computed: (r) => (Number(r.stock) || 0) * (Number(r.stdCost) || 0),
      },
      { key: "gst", label: "Tax Class", type: "string" },
      { key: "brand", label: "Make", type: "string" },
    ],
    []
  );

  /* ---------------- Helpers ---------------- */
  const uniqueValuesByColumn = useMemo(() => {
    const cache = {};
    for (const col of columns) {
      const s = new Set();
      for (const x of items) {
        const v =
          col.key === "value"
            ? (Number(x.stock) || 0) * (Number(x.stdCost) || 0)
            : x[col.key];
        s.add(v ?? "");
      }
      cache[col.key] = [...s].sort((a, b) => String(a).localeCompare(String(b)));
    }
    return cache;
  }, [items, columns]);

  const getUniqueValues = useCallback(
    (key) => uniqueValuesByColumn[key] || [],
    [uniqueValuesByColumn]
  );

  const rawFiltered = useMemo(() => {
    return items.filter((row) =>
      Object.keys(filters).every((key) => {
        if (key.startsWith("__search_")) return true;
        const val = filters[key];
        if (val === "" || val == null) return true;
        const cell =
          key === "value"
            ? (Number(row.stock) || 0) * (Number(row.stdCost) || 0)
            : row[key];
        return ("" + (cell ?? "")).toLowerCase() === ("" + val).toLowerCase();
      })
    );
  }, [items, filters]);

  const sorted = useMemo(() => {
    if (!sortConfig) return rawFiltered;
    const { key, direction } = sortConfig;
    const col = columns.find((c) => c.key === key);
    const compare = (a, b) => {
      const aval = col?.computed ? col.computed(a) : a[key];
      const bval = col?.computed ? col.computed(b) : b[key];
      return aval > bval ? 1 : aval < bval ? -1 : 0;
    };
    return [...rawFiltered].sort((a, b) =>
      direction === "asc" ? compare(a, b) : -compare(a, b)
    );
  }, [rawFiltered, sortConfig, columns]);

  const totalStock = useMemo(
    () => sorted.reduce((s, x) => s + (Number(x.stock) || 0), 0),
    [sorted]
  );
  const totalValue = useMemo(
    () =>
      sorted.reduce(
        (s, x) => s + (Number(x.stock) || 0) * (Number(x.stdCost) || 0),
        0
      ),
    [sorted]
  );

  const debouncedFilters = useDebouncedValue(filters, 180);

  useEffect(() => {
    const handler = (e) => {
      if (!openFilter) return;
      const ref = filterRefs.current[openFilter];
      if (ref && !ref.contains(e.target)) setOpenFilter(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openFilter]);

  const handleSort = (colKey) => {
    setSortConfig((prev) => {
      if (prev?.key === colKey) {
        if (prev.direction === "asc") return { key: colKey, direction: "desc" };
        if (prev.direction === "desc") return null;
      }
      return { key: colKey, direction: "asc" };
    });
  };

  const exportToExcel = () => {
    const exportData = sorted.map((row) => {
      const value = (Number(row.stock) || 0) * (Number(row.stdCost) || 0);
      return {
        Code: row.code,
        Description: row.desc,
        Group: row.group,
        Quantity: Number(row.stock) || 0,
        Unit: row.unit,
        Rate: Number(row.stdCost) || 0,
        Value: value,
        Tax: row.gst,
        Brand: row.brand,
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    XLSX.writeFile(wb, `StockReport_${stamp}.xlsx`);
  };

  const renderFilterDropdown = (col) => {
    const key = col.key;
    const searchKey = `__search_${key}`;
    const searchVal = debouncedFilters[searchKey] || filters[searchKey] || "";
    const options = getUniqueValues(key)
      .filter((val) =>
        ("" + val).toLowerCase().includes(searchVal.toLowerCase())
      )
      .slice(0, 1000);

    return (
      openFilter === key && (
        <div
          ref={(el) => (filterRefs.current[key] = el)}
          className="absolute right-0 mt-2 bg-white border shadow rounded z-20 max-h-64 w-48 flex flex-col text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            placeholder="Search..."
            className="px-2 py-1 border-b text-xs outline-none"
            value={filters[searchKey] || ""}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, [searchKey]: e.target.value }))
            }
          />
          <div className="overflow-y-auto flex-1 text-xs">
            {options.map((val, idx) => (
              <div
                key={idx}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    [key]: val === "" ? "" : val,
                    [searchKey]: prev[searchKey] || "",
                  }))
                }
                className={`px-2 py-1 hover:bg-blue-100 cursor-pointer ${String(filters[key] ?? "") === String(val)
                    ? "bg-blue-200 font-semibold"
                    : ""
                  }`}
              >
                {val === "" ? "(Blank)" : String(val)}
              </div>
            ))}
          </div>
          <div
            onClick={() =>
              setFilters((prev) => {
                const copy = { ...prev };
                copy[key] = "";
                copy[searchKey] = "";
                return copy;
              })
            }
            className="px-2 py-1 text-red-500 hover:bg-red-100 cursor-pointer text-xs border-t"
          >
            Clear
          </div>
        </div>
      )
    );
  };

  return (
    <div className="flex flex-col h-screen bg-[#f1f2f4]">
      <MenuBar />
      <HeaderBar />

      <div className="bg-[#dedfe3] border-x border-b border-gray-400 px-2 py-2 text-xs flex gap-2 items-center">
        <button type="button" className="px-3 py-1 text-xs bg-zinc-800 text-white">
          Stock Status (VIEW)
        </button>
        <Link
          to="/movement-analysis"
          className="px-3 py-1 text-xs bg-zinc-200 hover:bg-zinc-300"
        >
          Movement Analysis
        </Link>

        <div className="ml-4 flex items-center gap-2">
          <label className="text-xs">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="text-xs px-2 py-1 border rounded"
          />
          <label className="text-xs">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="text-xs px-2 py-1 border rounded"
          />
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            className="text-xs px-2 py-1 border rounded text-red-600"
          >
            Clear Dates
          </button>
        </div>

        <div className="ml-auto flex gap-2">
          <button
            onClick={exportToExcel}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded"
          >
            Export Excel
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto relative p-4">
        {loading && <div className="text-sm text-gray-600 mb-2">Loading stock from uploaded sales data…</div>}
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {!loading && !error ? (
          <p className="text-xs text-gray-600 mb-2">
            {rowCount.toLocaleString("en-IN")} parts from sales import
            {debouncedFromDate && debouncedToDate
              ? ` (${debouncedFromDate} → ${debouncedToDate})`
              : ""}
            . Narrow the date range for faster loads.
          </p>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full border border-gray-300 text-xs">
            <thead className="bg-gray-200 sticky top-0 z-10">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="border px-2 py-1 text-left relative cursor-pointer select-none"
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="flex items-center justify-between">
                      <span>{col.label}</span>
                      <div className="flex items-center gap-2">
                        {sortConfig?.key === col.key ? (
                          <small className="text-gray-600">
                            {sortConfig.direction === "asc" ? "▲" : "▼"}
                          </small>
                        ) : null}
                        <span
                          className="ml-1 text-gray-600 cursor-pointer px-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilter(
                              (prev) => (prev === col.key ? null : col.key)
                            );
                          }}
                        >
                          ☰
                        </span>
                      </div>
                    </div>
                    {renderFilterDropdown(col)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sorted.map((row, idx) => {
                const value =
                  (Number(row.stock) || 0) * (Number(row.stdCost) || 0);
                return (
                  <tr
                    key={row.id ?? idx}
                    className="odd:bg-white even:bg-gray-50"
                  >
                    <td className="border px-2 py-1">{row.code}</td>
                    <td className="border px-2 py-1">{row.desc}</td>
                    <td className="border px-2 py-1">{row.group}</td>
                    <td className="border px-2 py-1 text-right">
                      {(Number(row.stock) || 0).toFixed(2)}
                    </td>
                    <td className="border px-2 py-1">{row.unit}</td>
                    <td className="border px-2 py-1 text-right">
                      {(Number(row.stdCost) || 0).toFixed(2)}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {value.toFixed(2)}
                    </td>
                    <td className="border px-2 py-1">{row.gst ?? ""}%</td>
                    <td className="border px-2 py-1">{row.brand}</td>
                  </tr>
                );
              })}

              {sorted.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center text-gray-500 p-4 border"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="bg-gray-50 sticky bottom-0">
              <tr className="font-semibold">
                <td className="border px-2 py-1 text-right">
                  {items.length} Nos
                </td>
                <td className="border"></td>
                <td className="border"></td>
                <td className="border px-2 py-1 text-right">
                  {totalStock.toFixed(2)}
                </td>
                <td className="border"></td>
                <td className="border"></td>
                <td className="border px-2 py-1 text-right">
                  {totalValue.toFixed(2)}
                </td>
                <td className="border"></td>
                <td className="border"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
